import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ConsultationRequestHeaderComponent } from '@components/consultation-request-header/consultation-request-header.component';
import { RadioButtonsQuestionCardComponent } from '@components/radio-buttons-question-card/radio-buttons-question-card.component';
import { QuestionnaireAnswers } from '@enums/questionnaire-answers';
import { UpsellSlugs } from '@enums/upsell-slugs';
import { environment } from '@environments/environment';
import { SelectableOption } from '@models/selectable-option';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { FormService } from '@services/form.service';
import { NavigationService } from '@services/navigation.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-upsell-card',
  templateUrl: './upsell-card.component.html',
  styleUrls: ['./upsell-card.component.scss'],
  imports: [ConsultationRequestHeaderComponent, RadioButtonsQuestionCardComponent],
})
export class UpsellCardComponent implements OnInit, OnDestroy {
  @Input() public htmlTitle: string;
  @Input() public options: SelectableOption[];
  @Input() public question: string;
  @Input() public slug: UpsellSlugs;
  @Input() public hints: string[];
  @Input() public additionalFormsToValidate: FormGroup[] = [];
  @Output() public continueButtonPressed: EventEmitter<string> = new EventEmitter<string>();
  @Output() public optionSelected: EventEmitter<string> = new EventEmitter<string>();

  public readonly controlName: string = 'upsell';
  public form: FormGroup;
  public processing: boolean = false;

  private navigationService = inject(NavigationService);
  private formService = inject(FormService);
  private consultationStorageService = inject(ConsultationStorageService);
  private subscriptions: Subscription[] = [];

  /**
   * Gets the form control with the name set in the controlName property.
   *
   * @returns {FormControl} the form control
   */
  public get control(): FormControl {
    return this.form.get(this.controlName) as FormControl;
  }

  /**
   * Initializes the component by adding the validators to the form control.
   */
  public ngOnInit(): void {
    this.processing = false;
    this.setOptions();
    this.setHints();
    this.form = this.formService.getUpsellCardForm();
    this.markSelectedUpsell();
    this.listenToValueChanges();
  }

  /**
   * Unsubscribes from the value changes subscription.
   */
  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Asserts that the control is valid and navigates to the next page.
   */
  public async continue(): Promise<void> {
    const formsToValidate = [...this.additionalFormsToValidate, this.form];

    if (formsToValidate.some((form) => form.invalid)) {
      formsToValidate.forEach((form) => form.markAllAsTouched());

      return;
    }

    this.processing = true;
    this.consultationStorageService.upsells = this.updateUpsells(this.getUpsells(), this.control.value);
    this.continueButtonPressed.emit(this.control.value);
    this.navigationService.navigateToNextConsultationRequestPage(this.consultationStorageService.treatmentType);
  }

  /**
   * Sets selectable options for the question if they are not already set.
   */
  private setOptions(): void {
    this.options = this.options || [
      new SelectableOption(QuestionnaireAnswers.Yes, this.slug),
      new SelectableOption(QuestionnaireAnswers.No),
    ];
  }

  /**
   * Sets hints for the question if they are not already set.
   */
  private setHints(): void {
    this.hints = this.hints || [
      `Most patients select this option, and it comes with a small $${
        environment.consultationRequestUpsells[this.slug]?.price
      } additional charge.`,
    ];
  }

  /**
   * Returns an immutable copy of the upsells array.
   */
  private getUpsells(): string[] {
    return [...this.consultationStorageService.upsells];
  }

  /**
   * Updates the upsells array based on the answer.
   * If the answer is 'No', the upsells are removed from the array.
   * If the answer is 'Yes', the upsells are added to the array.
   *
   * @param {string[]} upsells The upsells array.
   * @param {string}   answer  The answer to the question.
   */
  private updateUpsells(upsells: string[], answer: string): string[] {
    upsells = this.removeUpsellOptions(upsells);

    return answer === QuestionnaireAnswers.No ? upsells : this.addUpsellOption(upsells, answer);
  }

  /**
   * Removes the upsell options from the upsells array.
   *
   * @param {string[]} upsells The upsells array.
   */
  private removeUpsellOptions(upsells: string[]): string[] {
    return upsells.filter((upsell) => !this.options.map((option) => option.value).includes(upsell));
  }

  /**
   * Adds the answer to the upsells array if it is not already included.
   *
   * @param {string[]} upsells The upsells array.
   * @param {string}   answer  The answer to the question.
   */
  private addUpsellOption(upsells: string[], answer: string): string[] {
    return [...upsells, answer];
  }

  /**
   * Marks the selected upsell option in the form control.
   * Useful when the user navigates back to the page.
   */
  private markSelectedUpsell(): void {
    const upsells = this.consultationStorageService.upsells;
    const selectedOption = this.options.find((option) => upsells.includes(option.value));

    if (selectedOption) {
      this.control.setValue(selectedOption.value);
    }
  }

  /**
   * Subscribes to form control value changes and emits the selected option.
   */
  private listenToValueChanges(): void {
    this.subscriptions.push(
      this.control.valueChanges.subscribe((value) => {
        this.optionSelected.emit(value);
      }),
    );
  }
}
